import api from '../utils/api';

/**
 * @typedef {Object} Discussion
 * @property {string} _id
 * @property {string} title
 * @property {string} content
 * @property {Object} author
 * @property {string} author._id
 * @property {string} author.username
 * @property {string} author.firstName
 * @property {string} author.lastName
 * @property {Object} course
 * @property {string} course._id
 * @property {string} course.title
 * @property {Object} [lesson]
 * @property {string} lesson._id
 * @property {string} lesson.title
 * @property {number} lesson.order
 * @property {boolean} isPinned
 * @property {boolean} isLocked
 * @property {boolean} isSolved
 * @property {any[]} upvotes
 * @property {any[]} replies
 * @property {number} views
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} Reply
 * @property {string} _id
 * @property {string} content
 * @property {Object} author
 * @property {string} author._id
 * @property {string} author.username
 * @property {boolean} isSolution
 * @property {any[]} upvotes
 * @property {string} createdAt
 */

/**
 * @typedef {Object} CreateDiscussionData
 * @property {string} title
 * @property {string} content
 * @property {string} courseId
 * @property {string} [lessonId]
 */

export const discussionService = {
  // ==================
  // PROTECTED ROUTES
  // ==================

  /**
   * Create discussion
   * @param {CreateDiscussionData} discussionData 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  createDiscussion: async (discussionData) => {
    const response = await api.post('/api/discussions', discussionData);
    return response;
  },

  /**
   * Get my discussions
   * @returns {Promise<{status: string, count: number, discussions: Discussion[]}>}
   */
  getMyDiscussions: async () => {
    const response = await api.get('/api/discussions/my/discussions');
    return response;
  },

  /**
   * Get discussions by course
   * @param {string} courseId 
   * @returns {Promise<{status: string, count: number, discussions: Discussion[]}>}
   */
  getDiscussionsByCourse: async (courseId) => {
    const response = await api.get(`/api/discussions/course/${courseId}`);
    return response;
  },

  /**
   * Get all discussions (Admin)
   * @param {Object} params - Query params (page, limit, search, course, etc.)
   * @returns {Promise<{status: string, count: number, discussions: Discussion[], pagination: Object}>}
   */
  getAllDiscussions: async (params) => {
    const response = await api.get('/api/discussions/admin/all', { params });
    return response;
  },

  /**
   * Get discussion by ID
   * @param {string} discussionId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  getDiscussionById: async (discussionId) => {
    const response = await api.get(`/api/discussions/${discussionId}`);
    return response;
  },

  /**
   * Update discussion
   * @param {string} discussionId 
   * @param {Object} updates 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  updateDiscussion: async (discussionId, updates) => {
    const response = await api.put(`/api/discussions/${discussionId}`, updates);
    return response;
  },

  /**
   * Delete discussion
   * @param {string} discussionId 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteDiscussion: async (discussionId) => {
    const response = await api.delete(`/api/discussions/${discussionId}`);
    return response;
  },

  /**
   * Add reply
   * @param {string} discussionId 
   * @param {string} content 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  addReply: async (discussionId, content) => {
    const response = await api.post(`/api/discussions/${discussionId}/replies`, { content });
    return response;
  },

  /**
   * Accept reply as solution
   * @param {string} discussionId 
   * @param {string} replyId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  acceptReply: async (discussionId, replyId) => {
    const response = await api.post(`/api/discussions/${discussionId}/replies/${replyId}/accept`);
    return response;
  },

  // ==================
  // VOTING
  // ==================

  /**
   * Upvote discussion
   * @param {string} discussionId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  upvoteDiscussion: async (discussionId) => {
    const response = await api.post(`/api/discussions/${discussionId}/upvote`);
    return response;
  },

  /**
   * Downvote discussion
   * @param {string} discussionId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  downvoteDiscussion: async (discussionId) => {
    const response = await api.post(`/api/discussions/${discussionId}/downvote`);
    return response;
  },

  /**
   * Upvote reply
   * @param {string} replyId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  upvoteReply: async (replyId) => {
    const response = await api.post(`/api/discussions/replies/${replyId}/upvote`);
    return response;
  },

  /**
   * Downvote reply
   * @param {string} replyId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  downvoteReply: async (replyId) => {
    const response = await api.post(`/api/discussions/replies/${replyId}/downvote`);
    return response;
  },

  // ==================
  // REPLY MANAGEMENT
  // ==================

  /**
   * Update reply
   * @param {string} replyId 
   * @param {string} content 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  updateReply: async (replyId, content) => {
    const response = await api.put(`/api/discussions/replies/${replyId}`, { content });
    return response;
  },

  /**
   * Delete reply
   * @param {string} replyId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  deleteReply: async (replyId) => {
    const response = await api.delete(`/api/discussions/replies/${replyId}`);
    return response;
  },

  // ==================
  // ADMIN/INSTRUCTOR ROUTES
  // ==================

  /**
   * Toggle pin discussion
   * @param {string} discussionId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  togglePinDiscussion: async (discussionId) => {
    const response = await api.patch(`/api/discussions/${discussionId}/toggle-pin`);
    return response;
  },

  /**
   * Toggle lock discussion
   * @param {string} discussionId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  toggleLockDiscussion: async (discussionId) => {
    const response = await api.patch(`/api/discussions/${discussionId}/toggle-lock`);
    return response;
  },

  /**
   * Toggle solved discussion
   * @param {string} discussionId 
   * @returns {Promise<{status: string, discussion: Discussion}>}
   */
  toggleSolvedDiscussion: async (discussionId) => {
    const response = await api.patch(`/api/discussions/${discussionId}/toggle-solved`);
    return response;
  },
};
